import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Heart, ArrowLeft, FileText, Scale, Users, Shield, CreditCard, AlertTriangle, Ban, CheckCircle } from "lucide-react";

const LOGO_URL = "/images/singleseen_logo_transparent.png";

const TermsPage = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-amber-50/30">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-rose-100/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-36">
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img src={LOGO_URL} alt="SingleSeen" className="h-36 w-auto" />
            </Link>
            <Link to="/" className="text-rose-600 hover:text-rose-700 flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-40 pb-16 bg-gradient-to-r from-rose-500 to-pink-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-6">
            <Scale className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl text-white mb-4">{t('terms.title')}</h1>
          <p className="text-rose-100 text-lg">{t('terms.subtitle')}</p>
          <p className="text-rose-200 text-sm mt-4">{t('terms.lastUpdated')}</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg border border-rose-100 p-8 md:p-12 space-y-10">
            
            {/* Introduction */}
            <div className="prose prose-rose max-w-none">
              <p className="text-rose-700 text-lg leading-relaxed">
                Welcome to SingleSeen. By accessing or using our Muslim matrimonial platform, you agree to be bound 
                by these Terms of Service. SingleSeen is designed exclusively for Muslims who are seriously seeking 
                marriage in accordance with Islamic principles.
              </p>
            </div>

            {/* Important Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-amber-800 mb-2">{t('terms.importantNoticeTitle')}</h3>
                  <p className="text-amber-700 text-sm">
                    SingleSeen is a matrimonial platform for serious marriage seekers only. Any use of this platform 
                    for dating, casual relationships, or any purpose other than seeking a halal marriage is strictly 
                    prohibited and will result in immediate account termination.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 1 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-rose-500" />
                </div>
                <h2 className="font-serif text-xl text-rose-900">{t('terms.section1Title')}</h2>
              </div>
              <div className="pl-13 space-y-4 text-rose-700">
                <p>{t('terms.section1Intro')}</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>{t('terms.section1Item1')}</li>
                  <li>{t('terms.section1Item2')}</li>
                  <li>{t('terms.section1Item3')}</li>
                  <li>{t('terms.section1Item4')}</li>
                  <li>{t('terms.section1Item5')}</li>
                  <li>{t('terms.section1Item6')}</li>
                  <li>{t('terms.section1Item7')}</li>
                </ul>
              </div>
            </div>

            {/* Section 2 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-emerald-500" />
                </div>
                <h2 className="font-serif text-xl text-rose-900">{t('terms.section2Title')}</h2>
              </div>
              <div className="pl-13 space-y-4 text-rose-700">
                <p>{t('terms.section2Intro')}</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>{t('terms.section2Item1')}</li>
                  <li>{t('terms.section2Item2')}</li>
                  <li>{t('terms.section2Item3')}</li>
                  <li>{t('terms.section2Item4')}</li>
                  <li>{t('terms.section2Item5')}</li>
                  <li>{t('terms.section2Item6')}</li>
                </ul>
              </div>
            </div>

            {/* Section 3 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <Ban className="w-5 h-5 text-red-500" />
                </div>
                <h2 className="font-serif text-xl text-rose-900">{t('terms.section3Title')}</h2>
              </div>
              <div className="pl-13 space-y-4 text-rose-700">
                <p>{t('terms.section3Intro')}</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>{t('terms.section3Item1')}</li>
                  <li>{t('terms.section3Item2')}</li>
                  <li>{t('terms.section3Item3')}</li>
                  <li>{t('terms.section3Item4')}</li>
                  <li>{t('terms.section3Item5')}</li>
                  <li>{t('terms.section3Item6')}</li>
                  <li>{t('terms.section3Item7')}</li>
                  <li>{t('terms.section3Item8')}</li>
                  <li>{t('terms.section3Item9')}</li>
                </ul>
              </div>
            </div>

            {/* Section 4 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-blue-500" />
                </div>
                <h2 className="font-serif text-xl text-rose-900">{t('terms.section4Title')}</h2>
              </div>
              <div className="pl-13 space-y-4 text-rose-700">
                <h3 className="font-semibold text-rose-800">{t('terms.section4SubTitle')}</h3>
                <ul className="list-disc pl-5 space-y-2">
                  <li>{t('terms.section4Sub1')}</li>
                  <li>{t('terms.section4Sub2')}</li>
                  <li>{t('terms.section4Sub3')}</li>
                  <li>{t('terms.section4Sub4')}</li>
                </ul>
                
                <h3 className="font-semibold text-rose-800 mt-6">{t('terms.section4OneTimeTitle')}</h3>
                <ul className="list-disc pl-5 space-y-2">
                  <li>{t('terms.section4OneTime1')}</li>
                  <li>{t('terms.section4OneTime2')}</li>
                </ul>
                
                <h3 className="font-semibold text-rose-800 mt-6">{t('terms.section4RefundTitle')}</h3>
                <p>
                  Refunds may be granted at our discretion in cases of technical issues or billing errors. 
                  Contact our support team within 7 days of purchase to request a refund.
                </p>
              </div>
            </div>

            {/* Section 5 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Shield className="w-5 h-5 text-purple-500" />
                </div>
                <h2 className="font-serif text-xl text-rose-900">{t('terms.section5Title')}</h2>
              </div>
              <div className="pl-13 space-y-4 text-rose-700">
                <p>{t('terms.section5Intro')}</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>{t('terms.section5Item1')}</li>
                  <li>{t('terms.section5Item2')}</li>
                  <li>{t('terms.section5Item3')}</li>
                  <li>{t('terms.section5Item4')}</li>
                  <li>{t('terms.section5Item5')}</li>
                </ul>
              </div>
            </div>

            {/* Section 6 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
                <h2 className="font-serif text-xl text-rose-900">{t('terms.section6Title')}</h2>
              </div>
              <div className="pl-13 space-y-4 text-rose-700">
                <ul className="list-disc pl-5 space-y-2">
                  <li>{t('terms.section6Item1')}</li>
                  <li>{t('terms.section6Item2')}</li>
                  <li>{t('terms.section6Item3')}</li>
                  <li>{t('terms.section6Item4')}</li>
                </ul>
              </div>
            </div>

            {/* Section 7 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-rose-500" />
                </div>
                <h2 className="font-serif text-xl text-rose-900">{t('terms.section7Title')}</h2>
              </div>
              <div className="pl-13 space-y-4 text-rose-700">
                <ul className="list-disc pl-5 space-y-2">
                  <li>{t('terms.section7Item1')}</li>
                  <li>{t('terms.section7Item2')}</li>
                  <li>{t('terms.section7Item3')}</li>
                  <li>{t('terms.section7Item4')}</li>
                  <li>{t('terms.section7Item5')}</li>
                  <li>{t('terms.section7Item6')}</li>
                </ul>
              </div>
            </div>

            {/* Section 8 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Scale className="w-5 h-5 text-emerald-500" />
                </div>
                <h2 className="font-serif text-xl text-rose-900">{t('terms.section8Title')}</h2>
              </div>
              <div className="pl-13 space-y-4 text-rose-700">
                <p>
                  These Terms are governed by the laws of England and Wales. Any disputes shall be 
                  resolved in the courts of England and Wales. If any provision of these Terms is 
                  found invalid, the remaining provisions will continue in effect.
                </p>
              </div>
            </div>

            {/* Section 9 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-500" />
                </div>
                <h2 className="font-serif text-xl text-rose-900">{t('terms.section9Title')}</h2>
              </div>
              <div className="pl-13 space-y-4 text-rose-700">
                <p>{t('terms.section9Intro')}</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>{t('terms.section9Item1')}</li>
                  <li>{t('terms.section9Item2')}</li>
                  <li>{t('terms.section9Item3')}</li>
                  <li>{t('terms.section9Item4')}</li>
                </ul>
                <p className="mt-4">
                  You may delete your account at any time through your account settings. 
                  Upon deletion, your data will be removed in accordance with our Privacy Policy.
                </p>
              </div>
            </div>

            {/* Contact */}
            <div className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl p-6 border border-rose-100">
              <h3 className="font-semibold text-rose-900 mb-2">{t('terms.questionsTitle')}</h3>
              <p className="text-rose-700 text-sm mb-4">
                If you have any questions about these Terms of Service, please contact us.
              </p>
              <p className="text-rose-600">
                Email: <a href="mailto:Matchmaker@singleseen.com" className="text-rose-500 hover:text-rose-600 font-medium">Matchmaker@singleseen.com</a>
              </p>
            </div>

            {/* Acceptance */}
            <div className="border-t border-rose-100 pt-8">
              <p className="text-rose-700 text-sm text-center">
                By creating an account or using SingleSeen, you acknowledge that you have read, 
                understood, and agree to be bound by these Terms of Service and our Privacy Policy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-rose-900 text-rose-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-400 to-pink-400 flex items-center justify-center">
                <Heart className="w-4 h-4 text-white fill-white" />
              </div>
              <span className="font-serif text-lg text-white">SingleSeen</span>
            </div>
            <div className="flex gap-8 text-sm">
              <Link to="/contact" className="hover:text-white transition-colors">{t('common.contactUs')}</Link>
              <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/help" className="hover:text-white transition-colors">Help Center</Link>
            </div>
            <p className="text-sm">© 2024 SingleSeen. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
